"use client"

import { useLanguage } from "@/contexts/language-context"
import { useFormatters } from "@/hooks/use-formatters"
import { parseDateOnly } from "@/lib/utils"
import type { ScheduledBudgetedHoursEntryFormData as Entry } from "@/lib/schemas"
import { isAfterLastDay, pendingEntries } from "@/lib/utils/scheduled-budgeted-hours"

export interface ScheduledHoursSummaryProps {
  /** The worker's whole list. Only pending changes are shown; the rest are in the engagement history. */
  entries: Entry[] | null | undefined
  /** The worker's effective last day as yyyy-MM-dd, or null when open-ended. */
  lastDay: string | null
  className?: string
}

/** A worker's pending budgeted hours changes: one on the label's line, several as a list under it. */
export const ScheduledHoursSummary = ({ entries, lastDay, className }: ScheduledHoursSummaryProps) => {
  const { dictionary } = useLanguage()
  const { formatDate } = useFormatters()
  const t = dictionary?.engagement?.workers

  const pending = pendingEntries(entries)
  if (pending.length === 0) {
    return null
  }

  const describe = (entry: Entry) =>
    (t?.hoursPerDayFrom || "{hours} h/day from {date}")
      .replace("{hours}", String(entry.hoursPerDay))
      .replace("{date}", formatDate(parseDateOnly(entry.effectiveDate))) +
    (isAfterLastDay(entry, lastDay) ? ` (${t?.afterLastDayOfWork || "after last day of work"})` : "")

  const label = `${t?.scheduled || "Scheduled"}:`

  return (
    <div className={`text-xs text-muted-foreground italic ${className ?? ""}`}>
      {pending.length === 1 ? (
        <p>{label} {describe(pending[0])}</p>
      ) : (
        <>
          <p>{label}</p>
          <ul className="mt-0.5 list-disc space-y-0.5 pl-7">
            {pending.map(entry => (
              <li key={entry.id ?? `new-${entry.effectiveDate}`}>{describe(entry)}</li>
            ))}
          </ul>
        </>
      )}
    </div>
  )
}
